import React, { useState, useRef, useEffect, useCallback } from "react";
import { useI18n } from "../hooks/useI18n";
import { cn } from "../lib/utils";
import { Globe } from "lucide-react";

const langKeyMap: Record<string, string> = {
  "pt-BR": "ptBR",
  "pt-PT": "ptPT",
  es: "es",
  en: "en",
};

const shortLabelMap: Record<string, string> = {
  "pt-BR": "BR",
  "pt-PT": "PT",
  es: "ES",
  en: "EN",
};

export function LanguageSelector() {
  const { lang, setLang, t, languages } = useI18n();
  const [open, setOpen] = useState(false);
  const [focusIdx, setFocusIdx] = useState(-1);
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setOpen(false);
    setFocusIdx(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, close]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const len = languages.length;
      if (e.key === "Escape") {
        close();
        buttonRef.current?.focus();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((prev) => (prev + 1) % len);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((prev) => (prev <= 0 ? len - 1 : prev - 1));
        return;
      }
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const item = languages[focusIdx];
        if (item) {
          setLang(item.code);
          close();
        }
      }
    },
    [languages, focusIdx, setLang, close],
  );

  const labelKey = `lang.${langKeyMap[lang] ?? "ptBR"}`;

  return (
    <div ref={ref} className="relative" onKeyDown={open ? handleKeyDown : undefined}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-label={t("common.language")}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
      >
        <Globe className="h-4 w-4" />
        <span className="md:hidden">{shortLabelMap[lang] ?? lang}</span>
        <span className="hidden md:inline">{t(labelKey)}</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full mt-1 z-50 min-w-[200px] rounded-md border border-border bg-popover p-1 shadow-lg"
        >
          {languages.map((l, i) => {
            const itemKey = `lang.${langKeyMap[l.code] ?? l.code}`;
            return (
              <button
                key={l.code}
                type="button"
                role="option"
                aria-selected={lang === l.code}
                onClick={() => {
                  setLang(l.code);
                  close();
                }}
                className={cn(
                  "flex w-full items-center rounded-sm px-3 py-2 text-sm transition-colors",
                  i === focusIdx && "ring-2 ring-accent",
                  lang === l.code
                    ? "bg-accent text-accent-foreground font-medium"
                    : "text-popover-foreground hover:bg-muted",
                )}
              >
                {t(itemKey)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
